import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Link, List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { EinstellungenService } from '../services/einstellungenService';
import { Gks02Service } from '../services/sabrinaService';
import { DatevSettings } from '../types/einstellungen';
import { DatevAttempt, DatevDispatch, DatevDispatchRequest, DatevSummary, Gks02Gebuehrenbescheid } from '../types/sabrina';

/**
 * Gemeinsame Beschriftungen fuer Tabellen-Chip, Auftragsergebnis und Historie.
 * Die Schluessel entsprechen den Backend-Statuswerten; UNKLAR ist bewusst
 * kein Erfolg und darf keinen automatischen Wiederholungsversand ausloesen.
 */
export const datevStatusLabels = {
  VORGEMERKT: 'Vorgemerkt', IN_BEARBEITUNG: 'In Bearbeitung', GESENDET: 'Gesendet',
  FEHLER: 'Fehlgeschlagen', UNKLAR: 'Unklar – bitte prüfen',
};

/**
 * Bestimmt die Auswaehlbarkeit einer Tabellenzeile in Gks02Page.tsx.
 * Ein leerer Rueckgabewert erlaubt die Auswahl; sonst dient der Text als Tooltip
 * an der gesperrten Checkbox. Fehlender Status wird nicht als "nie gesendet"
 * interpretiert. Dateizugriff und Versandberechtigung prueft das Backend erneut.
 */
export const datevBlockedReason = (bescheid: Gks02Gebuehrenbescheid, summary?: DatevSummary): string => {
  if (!bescheid.vollstaendig) return 'Bescheid ist noch unvollständig';
  if (!bescheid.dokumente?.some(d => d.mimeType?.toLowerCase() === 'application/pdf')) return 'Keine PDF-Dokumente vorhanden';
  if (!summary) return 'DATEV-Status noch nicht verfügbar';
  if (['VORGEMERKT', 'IN_BEARBEITUNG'].includes(summary.letzterVersuch?.status || '')) return 'Versand läuft bereits';
  return '';
};

/**
 * Kompakte Anzeige fuer die DATEV-Spalte der Gebuehrentabelle.
 * Zeigt den letzten Versuch, nicht den Zahlungsstatus: Ein fehlgeschlagener
 * Wiederholungsversand kann neben einem bereits bezahlten Bescheid stehen.
 */
export const DatevStatusChip: React.FC<{ summary?: DatevSummary }> = ({ summary }) => {
  const status = summary?.letzterVersuch?.status;
  return <Chip size="small" variant="outlined" label={status ? datevStatusLabels[status] : summary ? 'Nicht gesendet' : 'Status unbekannt'}
    color={status === 'GESENDET' ? 'success' : status === 'FEHLER' || status === 'UNKLAR' ? 'warning' : 'default'} />;
};

// Das Backend beruecksichtigt auch aeltere erfolgreiche oder unklare Versuche.
// Deshalb nicht nur den Status des letzten Versuchs fuer die Warnung auswerten.
const requiresConfirmation = (summary?: DatevSummary) =>
  !!summary?.wiederholungErforderlich;

const storageKey = 'gks_datev_auftrag';
/**
 * Stellt die noch verfolgte Anfrage innerhalb desselben Browser-Tabs wieder her.
 * Gespeichert wird der Request, nicht nur seine ID, damit ein unklarer POST nach
 * einem Reload exakt wiederholbar bleibt. Defekte Eintraege werden ignoriert.
 */
const storedRequest = (): DatevDispatchRequest | null => {
  try {
    const request = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    return request?.requestId && Array.isArray(request.bescheidIds) ? request : null;
  } catch {
    return null;
  }
};
/**
 * Erzeugt eine UUID-v4 als Idempotenzschluessel ohne zusaetzliche Bibliothek.
 * Eine neue ID steht fuer einen bewusst neuen Auftrag; technische Wiederholungen
 * muessen die alte ID behalten. Die Bitmasken setzen UUID-Version und -Variante.
 */
const requestId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const value = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
};

interface Props {
  /** Aktuell geladene Bescheide; fuer diese IDs wird der DATEV-Sammelstatus gelesen. */
  bescheide: Gks02Gebuehrenbescheid[];
  /** Versandfaehige Auswahl aus den Checkboxen der uebergeordneten Gebuehren-Seite. */
  selected: Gks02Gebuehrenbescheid[];
  /** Status nach Bescheid-ID; gemeinsame Grundlage fuer Tabelle und Versanddialog. */
  summaries: Record<number, DatevSummary>;
  /** Gibt neu geladene Statuswerte an Gks02Page fuer Checkboxen und Chips zurueck. */
  onSummaries: (summaries: Record<number, DatevSummary>) => void;
  /** Fordert die Elternseite auf, Bescheide und damit auch Zahlungsstatus neu zu laden. */
  onChanged: () => void;
}

/**
 * Versandbereich innerhalb von Gks02Page, keine eigenstaendige Route.
 *
 * GUI: Meldungen -> Versand-Aktionszeile -> Auftragsergebnisse -> Bestaetigungsdialog.
 * Die Elternseite besitzt die Tabellenauswahl; dieses Panel besitzt Dialog,
 * Konfiguration, Request und Fortschrittsverfolgung.
 *
 * Datenfluss: Einstellungen/Sammelstatus laden -> Auswahl bestaetigen ->
 * POST /api/sabrina/gks02/datev-versand -> Auftrag per GET verfolgen ->
 * Elternliste aktualisieren. E-Mails und Bezahlt-Markierung erfolgen nur im Backend.
 */
const DatevVersandPanel: React.FC<Props> = ({ bescheide, selected, summaries, onSummaries, onChanged }) => {
  // Empfaenger und technische Bereitschaft stammen vom Backend, nicht aus lokalen SMTP-Daten.
  const [settings, setSettings] = useState<DatevSettings | null>(null);
  // dialog steuert das Modal; confirmed gilt nur fuer die angezeigte Wiederholungsauswahl.
  const [dialog, setDialog] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // busy umfasst die Auftragserstellung, nicht die gesamte Hintergrundverarbeitung.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // dispatch bleibt nach Abschluss fuer die Ergebnisliste sichtbar, auch wenn
  // bezahlte Bescheide inzwischen aus dem Standardfilter der Elternseite verschwinden.
  const [dispatch, setDispatch] = useState<DatevDispatch | null>(null);
  // trackingId aktiviert Polling; retryRequest ermoeglicht eine identische
  // Wiederholung nach unklarem HTTP-Ergebnis, ohne versehentlich einen neuen Auftrag anzulegen.
  const [trackingId, setTrackingId] = useState<string | null>(() => storedRequest()?.requestId || null);
  const [retryRequest, setRetryRequest] = useState<DatevDispatchRequest | null>(() => storedRequest());
  // Timer verwenden aktuelle Eltern-Callbacks, ohne bei jedem Render neu zu starten.
  const callbacks = useRef({ onChanged, onSummaries });
  callbacks.current = { onChanged, onSummaries };
  // Der ID-Vergleich setzt die Bestaetigung nur bei inhaltlich veraenderter Auswahl zurueck.
  const selectedIds = selected.map(b => b.id).join(',');
  const repeat = selected.filter(b => requiresConfirmation(summaries[b.id]));
  const hasActiveDispatch = Object.values(summaries).some(s =>
    ['VORGEMERKT', 'IN_BEARBEITUNG'].includes(s.letzterVersuch?.status || ''));

  /**
   * Aktualisiert Empfaenger und Sammelstatus gemeinsam, z.B. ueber "Neu laden".
   * Die Services verwenden den authentifizierten API-Client. Object.fromEntries
   * macht die Statusliste fuer Tabellenzugriffe ueber summaries[bescheidId] nutzbar.
   */
  const refresh = useCallback(async () => {
    const [config, statuses] = await Promise.all([
      EinstellungenService.getDatev(),
      Gks02Service.getDatevSummaries(bescheide.map(b => b.id)),
    ]);
    setSettings(config);
    callbacks.current.onSummaries(Object.fromEntries(statuses.map(s => [s.bescheidId, s])));
  }, [bescheide]);

  // Initiales Laden und Abgleich bei neuer Bescheidliste. active verhindert, dass
  // eine ueberholte Antwort nach einem Listenwechsel oder Unmount den Status ersetzt.
  useEffect(() => {
    let active = true;
    Promise.all([EinstellungenService.getDatev(), Gks02Service.getDatevSummaries(bescheide.map(b => b.id))])
      .then(([config, statuses]) => {
        if (!active) return;
        setSettings(config);
        callbacks.current.onSummaries(Object.fromEntries(statuses.map(s => [s.bescheidId, s])));
      }).catch(() => {
        if (active) {
          // Ohne verlaesslichen Status sperrt die Elternseite die Auswahl vorsorglich.
          callbacks.current.onSummaries({});
          setError('DATEV-Konfiguration oder Status konnte nicht geladen werden.');
        }
      });
    return () => { active = false; };
  }, [bescheide]);

  // Eine Bestaetigung darf weder fuer eine neue Auswahl noch beim erneuten Oeffnen weitergelten.
  useEffect(() => { setConfirmed(false); }, [selectedIds, dialog]);

  // Laufende Auftraege anderer Tabs/Administratoren ebenfalls sichtbar halten.
  // Beim eigenen Auftrag uebernimmt der folgende Polling-Effekt diese Aktualisierung.
  useEffect(() => {
    if (!hasActiveDispatch || trackingId) return;
    const timer = setInterval(() => callbacks.current.onChanged(), 5000);
    return () => clearInterval(timer);
  }, [hasActiveDispatch, trackingId]);

  /**
   * Verfolgt GET /api/sabrina/gks02/datev-versand/{id} im Abstand von drei Sekunden.
   * setTimeout wird erst nach der Antwort geplant, damit Anfragen nicht ueberlappen.
   * Abschluss beendet Polling, entfernt den Tab-Eintrag und aktualisiert die Liste.
   * 404/Netzfehler sind kein Anlass fuer einen automatischen neuen Versandauftrag.
   */
  useEffect(() => {
    if (!trackingId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await Gks02Service.getDatevDispatch(trackingId);
        if (stopped) return;
        setError('');
        setDispatch(result);
        setRetryRequest(null);
        if (result.abgeschlossen) {
          sessionStorage.removeItem(storageKey);
          setTrackingId(null);
          callbacks.current.onChanged();
          return;
        }
        callbacks.current.onChanged();
      } catch (error: any) {
        if (stopped) return;
        if (error.response?.status === 404) {
          setError('Auftrag noch nicht gefunden. Bei unklarer Anfrage nur denselben Auftrag erneut anfragen.');
        } else {
          setError('Versandstatus konnte nicht geladen werden. Die Statusabfrage wird fortgesetzt.');
        }
      }
      if (!stopped) timer = setTimeout(poll, 3000);
    };
    poll();
    // Bei Auftragswechsel/Unmount keine weiteren Timer oder verspaeteten UI-Updates zulassen.
    return () => { stopped = true; clearTimeout(timer); };
  }, [trackingId]);

  /**
   * Einziger Einstieg fuer den Versand-POST und dessen bewusste technische Wiederholung.
   * Der Request wird vor dem Aufruf gesichert, weil ein Timeout nicht beweist,
   * dass das Backend ihn abgelehnt hat. 4xx-Antworten geben die Bedienung wieder
   * frei; bei 5xx/Netzfehlern bleiben ID und Request zur Statuspruefung erhalten.
   */
  const submit = async (request: DatevDispatchRequest) => {
    setBusy(true);
    setDialog(false);
    setError('');
    sessionStorage.setItem(storageKey, JSON.stringify(request));
    try {
      const result = await Gks02Service.createDatevDispatch(request);
      setDispatch(result);
      setRetryRequest(null);
      setDialog(false);
      setTrackingId(request.requestId);
      refresh().catch(() => setError('Auftrag angenommen; DATEV-Status konnte noch nicht aktualisiert werden.'));
    } catch (error: any) {
      if (error.response && error.response.status < 500) {
        sessionStorage.removeItem(storageKey);
        setTrackingId(null);
        setRetryRequest(null);
      } else {
        setRetryRequest(request);
        setTrackingId(request.requestId);
      }
      setError(error.response?.data?.message || 'Anfrageergebnis unklar. Keinen neuen Auftrag erstellen; Status prüfen oder dieselbe Anfrage wiederholen.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Handler von "Verbindlich senden": uebersetzt die sichtbare Auswahl in einen Request.
   * Wiederholungen werden je Bescheid bestaetigt. erwarteterEmpfaenger erlaubt dem
   * Backend, eine seit Dialoganzeige geaenderte globale Adresse abzulehnen.
   */
  const send = () => {
    if (!settings?.datevEmail || !selected.length || (repeat.length > 0 && !confirmed)) return;
    submit({
      requestId: requestId(), bescheidIds: selected.map(b => b.id),
      erneutBestaetigteBescheidIds: repeat.map(b => b.id), erwarteterEmpfaenger: settings.datevEmail,
    });
  };

  return (
    <Box mb={2}>
      {/*
        Oberer Rueckmeldebereich: HTTP-/Statusfehler erscheinen als Warnung.
        "Neu laden" aktualisiert nur Konfiguration und Status, sendet keine E-Mail.
      */}
      {error && <Alert severity="warning" sx={{ mb: 1 }} action={<Button onClick={() => refresh().then(() => setError('')).catch(() => {})}>Neu laden</Button>}>{error}</Alert>}
      {/* Fehlende Versandbereitschaft separat erklaeren und zur Admin-Einstellung verlinken. */}
      {settings && !settings.versandBereit && <Alert severity="info" sx={{ mb: 1 }}>
        DATEV-Versand ist nicht konfiguriert. <Link component={RouterLink} to="/einstellungen">Einstellungen öffnen</Link>
      </Alert>}
      {/*
        Aktionszeile mit Umbruch auf schmalen Bildschirmen.
        Der Hauptbutton oeffnet nur den Dialog; er sendet noch keinen Auftrag.
        Gesperrt bei fehlender Bereitschaft/Auswahl, ueber 100 Bescheiden oder
        solange die Anfrage bzw. ein bereits verfolgter Auftrag laeuft.
      */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Button variant="contained" disabled={!settings?.versandBereit || !selected.length || selected.length > 100 || busy || !!trackingId}
          onClick={() => setDialog(true)}>An DATEV senden ({selected.length})</Button>
        {/* Empfaenger auch ausserhalb des Dialogs sichtbar machen, damit das Versandziel klar bleibt. */}
        <Typography variant="body2" color="text.secondary">{settings?.datevEmail || 'Keine DATEV-Adresse hinterlegt'}</Typography>
        {/* Spinner bleibt waehrend HTTP-Aufruf UND Hintergrundverarbeitung sichtbar. */}
        {(busy || trackingId) && <CircularProgress size={20} />}
        {/* Nur bei unklarem Request-Ergebnis anbieten; submit erhaelt dieselbe ID und Auswahl. */}
        {retryRequest && <Button disabled={busy} onClick={() => submit(retryRequest)}>Dieselbe Anfrage erneut prüfen</Button>}
      </Box>
      {/* Erklaert das deaktivierte Mengenlimit, statt lediglich den Button zu sperren. */}
      {selected.length > 100 && <Alert severity="warning">Bitte höchstens 100 Bescheide pro Auftrag auswählen.</Alert>}
      {/*
        Ergebnisbereich bleibt auch nach dem letzten Poll sichtbar.
        Der Zaehler beruecksichtigt nur GESENDET; Fehler/unklare Ergebnisse werden
        pro Bescheid ausgegeben. Dadurch wird ein Teilerfolg nicht als Gesamterfolg dargestellt.
      */}
      {dispatch && <Box mt={2}>
        <Typography variant="subtitle2">
          Auftrag {dispatch.auftragId}: {dispatch.ergebnisse.filter(r => r.status === 'GESENDET').length} von {dispatch.ergebnisse.length} gesendet
          {dispatch.abgeschlossen ? ' – abgeschlossen' : ' – wird bearbeitet'}
        </Typography>
        <List dense>{dispatch.ergebnisse.map(result => <ListItem key={result.id}>
          <ListItemText primary={`${result.bescheidNummer || result.bescheidId}: ${datevStatusLabels[result.status]}`}
            secondary={result.fehler || `${result.empfaenger}${result.gesendetAm ? ' · ' + new Date(result.gesendetAm).toLocaleString('de-DE') : ''}`} />
        </ListItem>)}</List>
      </Box>}
      {/*
        Bestaetigungsdialog: open wird ausschliesslich vom lokalen dialog-State gesteuert.
        Schliessen per Hintergrund/Escape ist waehrend der Auftragserstellung gesperrt.
      */}
      <Dialog open={dialog} onClose={() => !busy && setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gebührenbescheide an DATEV senden</DialogTitle>
        <DialogContent>
          {/* Zieladresse und Hinweis auf die erst im Backend ausgeloeste Bezahlt-Markierung. */}
          <Typography gutterBottom>Empfänger: {settings?.datevEmail}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>Eine E-Mail pro Bescheid. Nach Annahme durch den SMTP-Server wird der Bescheid als bezahlt markiert. Dies bestätigt weder DATEV-Verarbeitung noch eine tatsächliche Zahlung.</Alert>
          {/* Vorschau genau der ausgewaehlten Bescheide mit PDF-Anzahl und Einzelbetrag. */}
          <List dense>{selected.map(b => <ListItem key={b.id}><ListItemText
            primary={b.nummerGebuehrenbescheid || `Bescheid ${b.id}`}
              secondary={`${b.dokumente.filter(d => d.mimeType?.toLowerCase() === 'application/pdf').length} PDF(s) · ${(b.summeGesamt || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`} /></ListItem>)}</List>
          {/* Die Summe dient zur Kontrolle der Auswahl, nicht zur Ausloesung einer Bankzahlung. */}
          <Typography gutterBottom>Gesamt: {selected.reduce((total, b) => total + (b.summeGesamt || 0), 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography>
          {/*
            Zusatzwarnung nur bei Wiederholungen: Die Checkbox setzt confirmed.
            Sie bestaetigt explizit die aufgefuehrten Bescheide, nicht alle kuenftigen Sendungen.
          */}
          {repeat.length > 0 && <>
            <Alert severity="warning">Bereits gesendet oder vorheriges Ergebnis unklar: {repeat.map(b => b.nummerGebuehrenbescheid || b.id).join(', ')}. Ein erneuter Versand kann einen Doppelimport verursachen.</Alert>
            <FormControlLabel control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />}
              label="Ich habe den bisherigen Versand geprüft und bestätige den erneuten Versand dieser Bescheide." />
          </>}
        </DialogContent>
        {/* Erst dieser primaere Dialogbutton ruft send() auf; Abbrechen erzeugt keinen Auftrag. */}
        <DialogActions>
          <Button onClick={() => setDialog(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={send} variant="contained" disabled={busy || !!trackingId || !selected.length || (repeat.length > 0 && !confirmed)}>
            Verbindlich senden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Detailansicht unterhalb einer aufgeklappten Bescheidzeile in Gks02Page.
 * Laedt GET /api/sabrina/gks02/bescheide/{id}/datev-versand nur fuer diesen
 * Bescheid. Die Elternseite setzt einen statusabhaengigen React-key, damit
 * die Historie nach einem Statuswechsel neu geladen wird.
 */
export const DatevHistory: React.FC<{ bescheidId: number }> = ({ bescheidId }) => {
  // null bedeutet "noch nicht geladen"; [] bedeutet "erfolgreich geladen, keine Versuche".
  const [history, setHistory] = useState<DatevAttempt[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    // Wie beim Sammelstatus verspaetete Antworten nach Unmount nicht mehr anzeigen.
    let active = true;
    Gks02Service.getDatevHistory(bescheidId).then(data => { if (active) setHistory(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [bescheidId]);
  // Unterschiedliche Zustaende bewusst trennen: fehlgeschlagene Abfrage ist keine leere Historie.
  if (error) return <Alert severity="warning">Versandhistorie nicht verfügbar.</Alert>;
  if (!history) return <CircularProgress size={18} />;
  if (!history.length) return <Typography variant="body2">Noch kein DATEV-Versand.</Typography>;
  // Je Versuch: Status/Empfaenger sowie Versand- oder Anforderungszeitpunkt,
  // ausloesender Nutzer, Wiederholungsbestaetigung und gegebenenfalls Fehlermeldung.
  return <List dense>{history.map(attempt => <ListItem key={attempt.id}>
    <ListItemText primary={`${datevStatusLabels[attempt.status]} · ${attempt.empfaenger}`}
      secondary={`${new Date(attempt.gesendetAm || attempt.angefordertAm).toLocaleString('de-DE')} · ${attempt.angefordertVon}${attempt.erneutBestaetigt ? ' · Erneut bestätigt' : ''}${attempt.fehler ? ' · ' + attempt.fehler : ''}`} />
  </ListItem>)}</List>;
};

export default DatevVersandPanel;
